import { useCallback, useMemo, useRef, useState, useEffect } from 'react'

import parseISO from 'date-fns/parseISO'

import {
  MenuApiType,
  // eslint-disable-next-line camelcase
  Menus_VisibilityEnum,
  RestaurantQuery,
  DateTimeRange,
  usePaginatedMenuItemsQuery,
  usePaginatedMenuItemsWithPopularItemsQuery,
  PaginatedMenuItemsWithPopularItemsQuery,
  PaginatedMenusInput,
  PaginatedMenuItemsQuery,
  PaginatedMenusSortCriteria,
  FilterCriteria
} from 'src/apollo/onlineOrdering'
import { useOOClient } from 'src/shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { useExperiment } from 'src/shared/components/common/ab_testing/ABTestContext'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'

import { MenuItem as MenuSectionItem } from 'public/components/default_template/menu_section/MenuSection'
import { getItemsFlat } from 'public/components/default_template/online_ordering/upsell/utils/upsellUtils'
import ToastProduct from 'public/components/online_ordering/ToastProduct'

import { useOptionalFulfillment } from './FulfillmentContext'

export type Restaurant = RestaurantQuery['restaurantV2'] & {
  __typename: 'Restaurant'
}
export type MenusResponse = PaginatedMenuItemsQuery['paginatedMenuItems']
export type Menus = NonNullable<MenusResponse['menus']>
export type MenuGroups = Menus[0]['groups'] & { __typename: 'MenuGroup' }
export type MenuGroup = MenuGroups[0] & { __typename: 'MenuGroup' }
export type MenuItem = (MenuGroup['items'] & { __typename: 'MenuItem' })[0]

const PAGE_SIZE = 100

// Module-level cache timestamp tracker
// Tracks when each query was last fetched for TTL checking
const cacheTimestamps = new Map<string, number>()
const CACHE_TTL_MS = 120000 // 2 minutes
const MAX_CACHE_ENTRIES = 100 // Safety net to prevent unbounded growth

// Clean up stale entries that are older than the TTL
const cleanupStaleEntries = () => {
  const now = Date.now()
  for (const [key, timestamp] of cacheTimestamps.entries()) {
    if (now - timestamp > CACHE_TTL_MS) {
      cacheTimestamps.delete(key)
    }
  }
}

// Set timestamp with cleanup - only call this when data is fetched from network
const setTimestampWithCleanup = (cacheKey: string) => {
  // Prevent unbounded growth: clean up stale entries if approaching limit
  if (cacheTimestamps.size >= MAX_CACHE_ENTRIES) {
    cleanupStaleEntries()

    // If still over limit after cleanup, evict oldest entry
    if (cacheTimestamps.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = cacheTimestamps.keys().next().value
      if (oldestKey) {
        cacheTimestamps.delete(oldestKey)
      }
    }
  }

  cacheTimestamps.set(cacheKey, Date.now())
}

export type Props = {
  respectAvailability?: boolean
  channelGuid?: string | null
  skipPopularItems?: boolean
  // Disables the hook entirely — returns an empty shell with no state.
  skip?: boolean
  // Suppresses only the Apollo network calls while preserving the full return
  // shape (sort, setSort, etc.). Use this instead of skip when the caller
  // needs to hold onto hook state (e.g. useEcommMenus in local mode).
  skipQuery?: boolean
  requirePagination?: boolean
  categorySlug?: string | null
  consolidateItemsByProduct?: boolean
  filterCriteria?: FilterCriteria | null
  includeAvailableFilters?: boolean
  includeProductOptionFilters?: boolean
  hideOutOfStockItems?: boolean
}

export const useOOMenus = ({
  respectAvailability = true,
  channelGuid,
  skipPopularItems = false,
  skip = false,
  skipQuery = false,
  requirePagination = false,
  categorySlug = null,
  consolidateItemsByProduct = false,
  filterCriteria = null,
  includeAvailableFilters = false,
  includeProductOptionFilters = false,
  hideOutOfStockItems
}: Props) => {
  const fulfillmentContext = useOptionalFulfillment()
  const fulfillmentData = fulfillmentContext?.fulfillmentData

  const { selectedLocation, restaurant, ooRestaurant, toastProduct } =
    useRestaurant()
  const ooClient = useOOClient()
  const hasMore = useRef(true)
  const hasNewFilters = useRef(false)
  const [filters, _setFilters] = useState<Set<string>>(new Set())
  const [sort, _setSort] = useState<PaginatedMenusSortCriteria[] | null>(null)
  const shortUrl = selectedLocation.shortUrl
  const restaurantGuid = selectedLocation.externalId
  const ooOnly = restaurant.config.isOnlineOrderingOnly ?? true
  const hasOOModule =
    ooRestaurant?.hasOnlineOrderingModule || ooRestaurant?.hasToastLocalModule
  const shouldSkip = skip || (ooOnly && !hasOOModule)

  // Experiment: test cache-first fetch policy for menu queries
  const { selectedVariant } = useExperiment('oo-menu-cache-first')
  const useMenuCacheFirst = selectedVariant === 'test'

  // Page size requested on the ecommerce channel, tunable without a deploy.
  // do-menus caps whatever we send at its own oo-do-menus-items-per-page-str value, so the effective page size is min(ecomm FF, do-menus FF)
  const { ooEcommItemsPerPage } = useFlags()
  const ecommPageSize = ooEcommItemsPerPage || PAGE_SIZE

  const setFilters: typeof _setFilters = useCallback((arg) => {
    hasMore.current = true
    hasNewFilters.current = true
    _setFilters(arg)
  }, [])

  const setSort: typeof _setSort = useCallback((arg) => {
    hasMore.current = true
    hasNewFilters.current = true
    _setSort(arg)
  }, [])

  // Mark as having new filters when filterCriteria changes (synchronous so it's
  // set before Apollo's networkStatus is checked in the same render)
  const prevFilterCriteria = useRef(filterCriteria)
  if (prevFilterCriteria.current !== filterCriteria) {
    prevFilterCriteria.current = filterCriteria
    hasMore.current = true
    hasNewFilters.current = true
  }

  const baseInput = useMemo(
    () => ({
      restaurantGuid,
      menuApi: MenuApiType.Do,
      visibility: Menus_VisibilityEnum.ToastOnlineOrdering,
      respectAvailability,
      hideOutOfStockItems:
        hideOutOfStockItems ??
        restaurant.config.ooConfig?.hideOutOfStockItems ??
        false,
      dateTime: fulfillmentData?.cartFulfillmentData.fulfillmentDateTime
    }),
    [
      restaurantGuid,
      respectAvailability,
      hideOutOfStockItems,
      restaurant.config.ooConfig?.hideOutOfStockItems,
      fulfillmentData?.cartFulfillmentData.fulfillmentDateTime
    ]
  )

  const isEcomm = toastProduct === ToastProduct.OOEcommerce

  const menusInput: PaginatedMenusInput = useMemo(
    () => ({
      ...baseInput,
      channelGuid:
        channelGuid ||
        (isEcomm && selectedLocation?.ecommMenuChannelGuid) ||
        undefined,
      offset: 0,
      limit: isEcomm ? ecommPageSize : undefined,
      filters:
        filters.size > 0
          ? Array.from(filters).map((filter) => filter.split(':'))
          : null,
      requirePagination: requirePagination || isEcomm,
      slug: categorySlug,
      consolidateItemsByProduct: consolidateItemsByProduct && isEcomm,
      productLevelPagination: isEcomm,
      sort: sort ?? undefined,
      includeSlugs: isEcomm,
      filterCriteria: filterCriteria ?? undefined,
      includeAvailableFilters: includeAvailableFilters || undefined,
      includeProductOptionFilters: includeProductOptionFilters || undefined
    }),
    [
      baseInput,
      channelGuid,
      isEcomm,
      ecommPageSize,
      selectedLocation?.ecommMenuChannelGuid,
      filters,
      requirePagination,
      categorySlug,
      consolidateItemsByProduct,
      sort,
      filterCriteria,
      includeAvailableFilters,
      includeProductOptionFilters
    ]
  )

  const {
    data: menuDataResp,
    error: menuErr,
    loading: menuLoading,
    fetchMore: menuFetchMore,
    previousData: menuPreviousData,
    networkStatus: menuNetworkStatus,
    refetch: menuRefetch
  } = usePaginatedMenuItemsQuery({
    client: ooClient,
    variables: { input: menusInput },
    skip:
      !restaurantGuid ||
      !shortUrl ||
      shouldSkip ||
      !skipPopularItems ||
      skipQuery,
    fetchPolicy:
      typeof window === 'undefined'
        ? 'network-only'
        : useMenuCacheFirst
        ? 'cache-first'
        : 'cache-and-network',
    onCompleted() {
      hasNewFilters.current = false
      // Only set timestamp when data is fetched from network
      if (useMenuCacheFirst) {
        const cacheKey = JSON.stringify(menusInput)
        setTimestampWithCleanup(cacheKey)
      }
    }
  })

  const {
    data: popularItemsData,
    loading: popularItemsLoading,
    fetchMore: popularItemsFetchMore,
    previousData: popularItemsPreviousData,
    networkStatus: popularItemsNetworkStatus,
    refetch: popularItemsRefetch
  } = usePaginatedMenuItemsWithPopularItemsQuery({
    client: ooClient,
    variables: {
      input: menusInput,
      popularItemsInput: {
        menusInput: baseInput,
        restaurantGuid: restaurantGuid
      }
    },
    skip:
      !restaurantGuid ||
      !shortUrl ||
      skipPopularItems ||
      shouldSkip ||
      skipQuery,
    fetchPolicy:
      typeof window === 'undefined'
        ? 'network-only'
        : useMenuCacheFirst
        ? 'cache-first'
        : 'cache-and-network',
    onCompleted() {
      hasNewFilters.current = false
      // Only set timestamp when data is fetched from network
      if (useMenuCacheFirst) {
        const cacheKey = JSON.stringify(menusInput)
        setTimestampWithCleanup(cacheKey)
      }
    }
  })

  // Cache TTL: Check if cache is stale on mount and refetch if needed
  useEffect(() => {
    if (useMenuCacheFirst && typeof window !== 'undefined') {
      const cacheKey = JSON.stringify(menusInput)
      const lastFetchTime = cacheTimestamps.get(cacheKey)
      const now = Date.now()

      // Check if cache is stale (older than 2 minutes)
      if (lastFetchTime && now - lastFetchTime > CACHE_TTL_MS) {
        // Cache is stale, refetch
        if (menuRefetch) {
          menuRefetch()
        }
        if (popularItemsRefetch) {
          popularItemsRefetch()
        }
      }
    }
  }, [useMenuCacheFirst, menusInput, menuRefetch, popularItemsRefetch])

  // Leverage the previous data from each query so we can continue to render data even while parameters are changing
  const menuData =
    menuDataResp ||
    popularItemsData ||
    menuPreviousData ||
    popularItemsPreviousData

  // Calculate total items from both standalone items and products
  const currentItemCount =
    (menuData?.paginatedMenuItems?.items?.length || 0) +
    (menuData?.paginatedMenuItems?.products?.length || 0)

  hasMore.current =
    menuData?.paginatedMenuItems?.totalCount !== undefined
      ? menuData?.paginatedMenuItems?.totalCount > currentItemCount
      : (menuLoading || popularItemsLoading) && !menuErr

  const _fetchMore = menuFetchMore || popularItemsFetchMore
  const fetchMore = useCallback(
    (offset: number) => {
      if (!hasMore.current) {
        return
      }

      _fetchMore({
        variables: { input: { ...menusInput, offset } },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          const newItemsCount =
            (fetchMoreResult.paginatedMenuItems.items?.length || 0) +
            (fetchMoreResult.paginatedMenuItems.products?.length || 0)

          if (newItemsCount < (menusInput.limit ?? PAGE_SIZE)) {
            hasMore.current = false
          }

          return Object.assign({}, prev, {
            paginatedMenuItems: {
              items: [
                ...(prev.paginatedMenuItems.items || []),
                ...(fetchMoreResult.paginatedMenuItems.items || [])
              ],
              products: [
                ...(prev.paginatedMenuItems.products || []),
                ...(fetchMoreResult.paginatedMenuItems.products || [])
              ],
              groupings: prev.paginatedMenuItems.groupings,
              availableFilters: prev.paginatedMenuItems.availableFilters,
              totalCount: menuData?.paginatedMenuItems.totalCount,
              menus: null,
              count: fetchMoreResult.paginatedMenuItems.count
            }
          })
        }
      })
    },
    [_fetchMore, menusInput, menuData?.paginatedMenuItems?.totalCount]
  )

  if (shouldSkip) {
    return { menus: [], popularItems: [] }
  }

  const loading =
    (menuLoading && !menuData && !menuPreviousData) ||
    (popularItemsLoading && !popularItemsData && !popularItemsPreviousData)
  if (loading) {
    return { loading: true }
  }

  if (menuErr) {
    return { error: menuErr }
  }

  const menuWrapper: MenusResponse | undefined =
    menuData?.paginatedMenuItems as MenusResponse | undefined
  const menus = menuWrapper?.menus?.filter(
    (menu) =>
      menu.groups?.length && menu.groups.some((group) => group?.items?.length)
  )

  const shouldPreorderItemBeVisible = (item: MenuSectionItem | null) => {
    const preorderRule = item?.timeBasedRules?.preorderRule
    if (!preorderRule) {
      return true
    }
    const { start, end } = preorderRule.orderDateRange as DateTimeRange
    const now = new Date(Date())
    return parseISO(start) <= now && (!end || parseISO(end) >= now)
  }

  // **TEMPORARY**
  // Filter out any items with preorder rules whose orderDateRanges are in the past or the future
  let mappedMenus: Menus | undefined = menus?.map((menu) => ({
    ...menu,
    groups: menu.groups?.map((group) => {
      if (!group?.items) {
        return group
      }
      return {
        ...group,
        items: group.items.filter((item) => shouldPreorderItemBeVisible(item))
      }
    })
  }))

  // Flatten single-group menus into one synthetic "Menu" entry for simpler nav.
  // length guard prevents [].every() from producing a menu when there are no items
  if (
    mappedMenus?.length &&
    mappedMenus.every((menu) => menu.groups?.length === 1)
  ) {
    const menuGroups: MenuGroups = mappedMenus
      .flatMap((menu) => menu.groups)
      .filter(Boolean) as MenuGroups

    mappedMenus = [
      {
        name: 'Menu',
        guid: 'Menu',
        groups: menuGroups
      }
    ]
  }

  // network status 2 indicates that variables have changed; loadingNewFilterData should only be true if the queries wrapped in this hook
  // are returning new data because filters have changed
  const loadingNewFilterData =
    ((menuLoading && menuNetworkStatus === 2) ||
      (popularItemsLoading && popularItemsNetworkStatus === 2)) &&
    hasNewFilters.current

  // Compute different item arrays based on products and items
  const products = menuData?.paginatedMenuItems.products
  const items = menuData?.paginatedMenuItems.items

  // Flatten all variants from products into a single array
  const variantsFromProducts = products?.flatMap(
    (product) => product.variants ?? []
  )

  // Combine items with all variants from products
  const menuItems =
    items != null || variantsFromProducts != null
      ? [...(items ?? []), ...(variantsFromProducts ?? [])]
      : undefined

  if (skipPopularItems)
    return {
      menus: mappedMenus,
      menuItems,
      products,
      groupings: menuData?.paginatedMenuItems.groupings, // only populated when menu pagination is enabled, use menus otherwise
      availableFilters: menuData?.paginatedMenuItems.availableFilters,
      fetchMore,
      filters,
      setFilters,
      sort,
      setSort,
      totalCount: menuData?.paginatedMenuItems?.totalCount,
      loading,
      loadingNewFilterData,
      hasMore: hasMore.current
    }

  const popularItemsQuery: PaginatedMenuItemsWithPopularItemsQuery | undefined =
    popularItemsData
  let popularItems: Array<MenuSectionItem> = []
  if (
    popularItemsQuery?.popularItems &&
    'items' in popularItemsQuery.popularItems
  ) {
    popularItems = popularItemsQuery.popularItems.items
  }

  if (
    selectedLocation.featuredItems?.enabled &&
    !selectedLocation.featuredItems?.useDefaults &&
    mappedMenus
  ) {
    const menuItemMasterIds = new Set(
      selectedLocation.featuredItems.menuItemMasterIds
    )
    const items = getItemsFlat(mappedMenus)

    popularItems = [
      ...items.filter((item) => menuItemMasterIds.has(item.masterId)),
      ...popularItems.filter(
        (item) => !menuItemMasterIds.has(item.masterId || '')
      )
    ].slice(0, 3)
  }

  // **TEMPORARY**
  // Filter out any popular items with preorder rules whose orderDateRanges are in the past or the future
  popularItems = popularItems.filter((item) =>
    shouldPreorderItemBeVisible(item)
  )

  return {
    menus: mappedMenus,
    popularItems,
    menuItems,
    products,
    groupings: menuData?.paginatedMenuItems.groupings, // only populated when menu pagination is enabled, use menus otherwise
    availableFilters: menuData?.paginatedMenuItems.availableFilters,
    fetchMore,
    filters,
    setFilters,
    sort,
    setSort,
    totalCount: menuData?.paginatedMenuItems?.totalCount,
    loading,
    loadingNewFilterData,
    hasMore: hasMore.current
  }
}
